import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { action, observable, toJS, makeAutoObservable } from 'mobx';
import { Value } from 'src_common/common/mobx-utils/Value';
import { ApiGetRunsResponse200Type } from 'src/api_openapi/v2/automations/apiGetRuns';
import { AppState } from 'src/AppState/AppState';
import { timeout } from 'src_common/common/mobx-utils/timeout';
import { CronError } from '@levashovn/react-js-cron-mui5';
import { computed } from 'src_common/common/mobx-wrapper';
import { AutomationItemPropsType } from 'src/components/AutomationTrading/AutomationItem/AutomationItem';
import { AutotraderRunType } from 'src/api_openapi/v2/automations/schemas';

export type OptionalFilterType = AutotraderRunType['parameters'];
export type FilterType = Exclude<AutotraderRunType['parameters'], undefined>['filter']['kind'];

type ArrayParseResultType = {
    value: Array<string>;
    type: 'success';
};

export function formatAndCheckStringArray(value: string): ArrayParseResultType {
    value = value.charAt(0) === '[' ? `${value.slice(1)}` : value;
    value = value.charAt(value.length - 1) === ']' ? `${value.slice(0, -1)}` : value;
    const valueArray = value
        .split(',')
        .map((item) => `${item.trim().toString()}`)
        .map((item) => item.replace(/["']/g, ''))
        .filter((item) => item !== '');

    return { value: valueArray, type: 'success' };
}

export class AutomationItemState {
    @observable public changeNameInputActive: boolean = false;
    @observable public optionalFilterData: OptionalFilterType | null = null;
    public scheduleValue: string;
    private activeAutomationItem: FormInputState<boolean, boolean>;
    public error: null | string = null;
    public nameInputState: FormInputState<string, string>;
    public history: Value<ApiGetRunsResponse200Type | null>;
    public parametrizedInput: FormInputState<string, unknown> = FormInputState.new('');

    public constructor(
        private readonly props: AutomationItemPropsType,
        private readonly appState: AppState
    ) {
        this.scheduleValue = props.automationItem.schedule?.cron ?? '';

        this.history = new Value<ApiGetRunsResponse200Type | null>(null, (setValue): (() => void) => {
            let isConnect = true;

            setTimeout(async (): Promise<void> => {
                while (isConnect) {
                    try {
                        const resp = await this.appState.sdkStaff.trading.autotrader.getAutotraderRuns({
                            automationId: this.props.automationItem.id,
                        });
                        if (resp.status === 200) {
                            setValue(resp.body);
                        }
                    } catch (error) {
                        console.error(error);
                    }

                    await timeout(5000);
                }
            }, 0);

            return () => {
                isConnect = false;
            };
        });

        this.activeAutomationItem = FormInputState.new(this.props.automationItem.active);
        this.nameInputState = FormInputState.new(this.props.automationItem.name);
        makeAutoObservable(this);
    }

    public setError = (e: CronError): void => {
        this.error = e?.description ?? null;
    };
    @action public setValue = async (e: string): Promise<void> => {
        this.scheduleValue = e;

        this.error = null;
    };

    @action public onResetScheduler = async (): Promise<void> => {
        this.scheduleValue = '@daily';

        await this.appState.sdkStaff.trading.autotrader.patchAutoTraderAutomationFeed({
            automationId: this.props.automationItem.id,
            requestBody: {
                schedule: null,
            },
        });

        await this.props.automationState.allAutomations.refresh();
    };

    @action public onChangeSchedulerActive = async (): Promise<void> => {
        await this.appState.sdkStaff.trading.autotrader.patchAutoTraderAutomationFeed({
            automationId: this.props.automationItem.id,
            requestBody: {
                schedule: { cron: this.scheduleValue, kind: 'cron' },
            },
        });

        await this.props.automationState.allAutomations.refresh();
    };

    @action public onChangeItemActive = async (): Promise<void> => {
        this.activeAutomationItem.setValue(!this.activeAutomationItem.value);
        await this.appState.sdkStaff.trading.autotrader.patchAutoTraderAutomationFeed({
            automationId: this.props.automationItem.id,
            requestBody: {
                active: this.activeAutomationItem.value,
            },
        });

        await this.props.automationState.allAutomations.refresh();
    };

    public get typeOfAction(): 'events' | 'markets' | 'wait' {
        const actions = this.props.automationItem.actions[0];
        if (actions !== undefined) {
            return actions.kind;
        }

        return 'events';
    }

    public saveOptionalFilter = (
        type: FilterType,
        fieldKey: string | undefined,
        fieldValue: string,
        valueIn: string
    ): void => {
        if (valueIn.trim() === '' || fieldKey === undefined) {
            return;
        }
        if (type === 'in') {
            const parsedArray = formatAndCheckStringArray(valueIn);
            this.optionalFilterData = {
                [fieldKey]: {
                    kind: 'in',
                    in: {
                        field: fieldValue,
                        values: parsedArray.value,
                    },
                },
            };

            return;
        }
        console.warn('Not implemented yet');
    };

    public runAutomation = async (): Promise<void> => {
        await this.appState.sdkStaff.trading.autotrader.createAutotraderRun({
            automationId: this.props.automationItem.id,
            requestBody: {
                parameters: this.optionalFilterData === null ? undefined : toJS(this.optionalFilterData),
            },
        });
        this.optionalFilterData = null;
        this.parametrizedInput.reset();
    };

    public saveName = async (): Promise<void> => {
        await this.appState.sdkStaff.trading.autotrader.patchAutoTraderAutomationFeed({
            automationId: this.props.automationItem.id,
            requestBody: {
                name: this.nameInputState.value,
            },
        });
        this.changeNameInputActive = false;
        await this.props.automationState.allAutomations.refresh();
    };

    @computed public get runsHistory(): ApiGetRunsResponse200Type | null {
        return this.history.getValue();
    }

    @action public changeNameActive = (): void => {
        this.changeNameInputActive = true;
    };
}
